// What a printed quote's body is made of: the ordered list of headings,
// sub-headings and lines, worked out from the print options.
//
// Lifted out of QuoteView.jsx so it can be tested, because the rule it encodes
// is easy to get subtly wrong and the output goes to clients. Rendering stays in
// the component; deciding WHAT rows exist, and in what order, is here.
//
// Legacy source: `PresentationLayer/Reports/QuoteReport.aspx.cs`, the loop from
// roughly line 214. Its two radio buttons default to `rblHeader = Ignore` and
// `rblOrderBy = Entered`, which together are the "location" layout below — the
// layout 1.0 prints unless somebody changes it, and the one Karen Sweatman asked
// for on 2026-08-11.

/** "Ground Floor — Hallway 1", or "" when the line carries neither. */
export const locationOf = (line) =>
  [line.floor_name, line.room_name].filter(Boolean).join(" — ");

/**
 * The sell/cost/margin split for the Vero-style summary ("Separate margin"):
 * cost = sell − margin so the three ALWAYS reconcile to the cent with the
 * standard print's ex-GST total, and the printed percent is MARGIN of sell
 * (57% markup prints as 36.3) — Vero asked for margin, not markup.
 */
export function splitTotals(lines = []) {
  const margin = +lines.reduce((s, l) => s + Number(l.margin_value || 0), 0).toFixed(2);
  const sell = +lines.reduce((s, l) => s + Number(l.net_value || 0), 0).toFixed(2);
  return {
    margin,
    sell,
    cost: +(sell - margin).toFixed(2),
    marginPct: sell ? +((margin / sell) * 100).toFixed(1) : 0,
  };
}

/**
 * @param {Array} body   the option-filtered lines, in display order
 * @param {object} opts  { detail, headings, roomTotals, amountKey }
 * @param {Array} headerSummaries  doc.summaries.header, for summaryOnly
 * @returns {Array} [{ kind: "heading"|"subheading"|"line"|"sectionTotal"|"summary", … }]
 *   (a heading with `header: true` is one of the quote's own header lines)
 */
// A quote header's text split into [title, body] at its first line break —
// a newline (2.0's editor) or 1.0's two spaces, whichever comes first;
// no break = title only. QuoteView re-exports it for the print and the test.
export function splitHeaderText(label = "") {
  const text = String(label).replace(/\r/g, "");
  const nl = text.indexOf("\n");
  const sp = text.indexOf("  ");
  const cut = [nl, sp].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (cut === undefined) return [text.trim(), ""];
  const sep = text.startsWith("\n", cut) ? 1 : 2;
  return [text.slice(0, cut).trim(), text.slice(cut + sep).trim()];
}
// A header that carries a description ("Cabinet Toe Kick - Remove…  Carefully
// remove, dispose…") is an ITEM in header's clothing — 1.0-style data puts every
// item's title on a header line with the priced note beneath. It prints like
// the other items: bold title and body inside the description column, amount
// blank. Only a title-only header (GENERAL, LOUNGE, UNIT TWO) is a heading that
// spans the page. Karen Renall, 76460, 2026-09-10: "the wording in the
// description column to be kept within column and not extend out into the
// costs column".
const hasBody = (label) => !!splitHeaderText(label)[1];

export function buildQuoteRows(body = [], opts = {}, headerSummaries = []) {
  if (opts.detail === "summaryOnly") {
    // 1.0's HeaderOnly: one row per header section with its subtotal. A
    // title-only header with nothing under it and no price of its own is a
    // bare heading (1.0 prints "GENERAL" that way), not a $0.00 line; a header
    // with a description keeps the item shape (see hasBody). Hide zero amounts
    // applies here as it does to a line: a $0 section keeps its title and
    // loses the "$0.00" (Robert 2026-09-07, 76511's ROOF INCLUDING FRAMING).
    return headerSummaries.map((h) =>
      ((h.count === 0 && !h.total) || (opts.hideZero && !h.total)) && !hasBody(h.label)
        ? { kind: "heading", label: h.label }
        : { kind: "summary", label: h.label, total: h.total }
    );
  }

  if (opts.headings !== "location") {
    // 1.0's ShowBreakdown: the quote's own header lines, inline where they sit.
    return body.map((l) =>
      l.is_header
        ? hasBody(l.description) ? { kind: "summary", label: l.description, total: null, header: true } : { kind: "heading", label: l.description || "—" }
        : { kind: "line", l }
    );
  }

  // RUNNING headers, in the order the lines were entered — NOT a sort into
  // groups, and the two differ whenever a quote returns to a room. 1.0 walks the
  // rows in `E.DisplayOrder` and emits a heading each time the value CHANGES
  // from the previous line, so Hallway → Toilet → Hallway prints three headings
  // and the quote reads in the order it was built. Sorting into groups instead
  // silently reorders somebody's quote, which is not ours to do.
  //
  // roomTotals (the Vero layout): each heading is numbered 1.00, 2.00, … and
  // closes with a "Total for …" row summing opts.amountKey (gross for the
  // costs-print, net otherwise). A section a quote RETURNS to totals each
  // visit separately — same running-heading rule, so the entered order stays.
  const rows = [];
  const amountKey = opts.amountKey || "net_value";
  let lastLocation = null;
  let lastWorkCentre = null;
  let sectionSum = 0;
  let sectionLabel = null;
  let sectionNo = 0;
  const closeSection = () => {
    if (opts.roomTotals && sectionLabel != null) {
      rows.push({ kind: "sectionTotal", label: sectionLabel, total: +sectionSum.toFixed(2) });
    }
    sectionSum = 0;
  };

  for (const l of body) {
    if (l.is_header) {
      // The quote's own header lines print HERE TOO (Robert 2026-09-10, from
      // Robyn's 76161: a body-corporate job whose rooms are "Misc 1 / Misc 2"
      // placeholders and whose real structure — UNIT TWO, UNIT EIGHT — was
      // typed as headers; this layout dropped them, the other dropped the
      // trades). A header outranks the location: it closes the running
      // section and the floor–room heading prints again beneath it.
      closeSection();
      sectionLabel = null;
      lastLocation = null;
      lastWorkCentre = null;
      // a header with a description keeps the item shape (hasBody); a title-only one heads the page
      rows.push(hasBody(l.description)
        ? { kind: "summary", label: l.description, total: null, header: true }
        : { kind: "heading", label: l.description || "—", header: true });
      continue;
    }
    const location = locationOf(l);

    // A line with no location AND no money is dropped entirely — 1.0:
    //   if (floorRoom == " - ") { if (amount == 0) continue; }
    // These are the leftovers of quote building: a blank row someone tabbed
    // past, a note with no home. Without this they print as a bare "—" under
    // whichever room happened to come before them, which is what job 76276
    // showed (Robert 2026-08-11). A no-location line that DOES carry an amount
    // still prints, because money always has to be accounted for.
    if (!location && !Number(l.net_value)) continue;

    // No floor and no room = no heading, but the line still prints. 1.0 skips
    // the heading for an empty "floor - room" and keeps the row; inventing a
    // "(No location)" section puts a made-up title on a client's quote.
    //
    // Under roomTotals a no-location line also CLOSES the section: the auto
    // fee lines carry no floor/room, and without this they were counted into
    // whichever room happened to print last ("Total for Misc 1" swallowing
    // the Overheads — Robert 2026-08-20, job 76481). They print after the
    // last room's total, in their own untotalled trailing block.
    if (!location && opts.roomTotals && sectionLabel != null) {
      closeSection();
      sectionLabel = null;
      lastLocation = null; // a located line after this opens a fresh section
    }
    if (location && location !== lastLocation) {
      closeSection();
      sectionLabel = location;
      sectionNo += 1;
      rows.push({
        kind: "heading",
        label: location,
        ...(opts.roomTotals ? { num: `${sectionNo}.00` } : {}),
      });
      lastLocation = location;
      lastWorkCentre = null; // a work centre repeats under each new location
    }
    if (l.work_centre_name && l.work_centre_name !== lastWorkCentre) {
      rows.push({ kind: "subheading", label: l.work_centre_name });
      lastWorkCentre = l.work_centre_name;
    }
    rows.push({ kind: "line", l });
    sectionSum += Number(l[amountKey] || 0);
  }
  closeSection();
  return rows;
}
